export const THUMBNAIL_WIDTHS = [160, 400] as const;
export const BANNER_WIDTHS = [400, 800] as const;

/**
 * Which `public/` directories have generated variants, and at which widths.
 *
 * **This is the single source of truth**, imported by
 * `scripts/generate-image-variants.ts` so the generator and the srcset builders can't drift. They
 * used to be two hand-maintained lists, and the failure mode is nasty and silent: a srcset that
 * names a variant nobody generated 404s only for the visitors whose viewport happens to select it.
 *
 * Adding a directory here does nothing on its own — re-run the generator
 * (`npx tsx scripts/generate-image-variants.ts`) and commit the variants, or the references break.
 */
export const VARIANT_DIRS: { dir: string; widths: readonly number[] }[] = [
  { dir: "/icons/", widths: THUMBNAIL_WIDTHS },
  { dir: "/wild-animals/", widths: THUMBNAIL_WIDTHS },
  // Both, because location art is rendered as a 161px card on /locations and full-bleed on detail.
  { dir: "/locations/", widths: [...THUMBNAIL_WIDTHS, ...BANNER_WIDTHS] },
  { dir: "/guides/", widths: BANNER_WIDTHS },
  // The four below are only ever reached through ContentBlock images (news posts and guide bodies),
  // never a component of their own — see ImageWithCaption.
  { dir: "/events/", widths: BANNER_WIDTHS },
  { dir: "/news/", widths: BANNER_WIDTHS },
  { dir: "/bubble14/", widths: BANNER_WIDTHS },
  { dir: "/bubblesFeb/", widths: BANNER_WIDTHS },
];

/**
 * Picks the right srcset for an arbitrary content-authored image path.
 *
 * ContentBlock images (`ImageWithCaption`) are the one call site that can't know its own directory
 * up front — a guide body mixes `/guides/` banners with `/icons/` thumbnails in the same article,
 * and those have different variant sets. Anything from an unlisted directory gets no srcset at all,
 * which is the safe answer: a plain `src` always works.
 */
export function buildContentSrcSet(path: string): string | undefined {
  if (!isLocalWebp(path)) return undefined;
  const entry = VARIANT_DIRS.find(({ dir }) => path.startsWith(dir));
  if (!entry) return undefined;
  return entry.widths.includes(800) ? buildBannerSrcSet(path) : buildResponsiveSrcSet(path);
}

/**
 * Builds a `srcset` for a `.webp` path that has `-160w`/`-400w` variants
 * generated by `scripts/generate-image-variants.ts` (see that script for
 * why every source always has both — a conditional srcset referencing a
 * variant that doesn't exist would 404 if the browser picked it).
 *
 * Returns undefined for anything that isn't a bare `.webp` path (external
 * URLs, other formats) so callers can spread it straight into <img> props.
 */
export function buildResponsiveSrcSet(path: string): string | undefined {
  if (!isLocalWebp(path)) return undefined;
  const base = path.slice(0, -".webp".length);
  return `${base}-160w.webp 160w, ${base}-400w.webp 400w, ${path} 900w`;
}

/**
 * Same idea for images rendered large — guide banners, the location detail hero — where the
 * thumbnail set above is worse than useless.
 *
 * A banner shows at ~327 CSS px on a phone, which is 654 device px at DPR 2. With only 160w and
 * 400w to choose from, the browser correctly rejects both and falls back to the full-size original
 * (210 KB for gardening-banner.webp) — so the srcset is present, looks right in the markup, and
 * changes nothing. The 800w step is what actually gets picked there.
 *
 * Requires `-400w`/`-800w` variants, i.e. a directory listed with BANNER_WIDTHS in VARIANT_DIRS
 * above. Using this on a directory that only has thumbnail variants would reference an 800w file
 * that doesn't exist and 404 when the browser picks it — use buildContentSrcSet when the directory
 * isn't known at the call site.
 */
export function buildBannerSrcSet(path: string): string | undefined {
  if (!isLocalWebp(path)) return undefined;
  const base = path.slice(0, -".webp".length);
  // Sources measured 282–1920px wide (median ~1024), so 1600w for the original is a deliberate
  // over-estimate: it keeps the original in play for large/retina viewports without letting the
  // browser pick it on a phone, which is the whole point.
  return `${base}-400w.webp 400w, ${base}-800w.webp 800w, ${path} 1600w`;
}

function isLocalWebp(path: string): boolean {
  return path.endsWith(".webp") && !/^https?:\/\//.test(path);
}
